/* eslint-disable quote-props */
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, SimpleChanges, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { FallbackSummaryMap, FallbackSummaries } from 'e2e/src/shared/constants';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { problemsCopy } from '../instant-checkup/problem-details';
import { AppConfig } from '../app.config';
import { extractRegimenProducts, extractHeroIngredients } from 'client/helper/regimen-helper';
import { ApiClientConstant } from '@cureskin/api-client';
import { PRIVATE_MAIN_CONCERN_DISPLAY_NAME } from '../user-regimen/regimen-products-detail/doctor-notes-constants';
import { ExperimentService } from '@services/experiment';

/** Extended mapping for treatment concern keys to display names (includes additional keys not in PRIVATE_MAIN_CONCERN_DISPLAY_NAME) */
const TREATMENT_CONCERN_KEY_TO_DISPLAY_NAME: Record<string, string> = {
  COMEDONES: 'Comedones',
  DRY_SKIN: 'Dry Skin',
  FACIAL_HAIR: 'Facial Hair',
  HAIR_FALL: 'Hair Fall',
  PRE_WEDDING_FACE: 'Pre Wedding Face',
  PRE_WEDDING_HAIR: 'Pre Wedding Hair',
  ...PRIVATE_MAIN_CONCERN_DISPLAY_NAME.en,
};

export interface SkinCareData {
  collagenImprovement: string;
  inflammationControl: string;
  skinRenewal: string;
  glycationReduction: string;
  sunProtection: string;
  antiOxidation: string;
}

interface SkinParameters {
  patientScore: number;
  expectedRange: string;
}

interface SkinData {
  sebumBalanceParameters: SkinParameters;
  inflammationParameters: SkinParameters;
  sensitivityParameters: SkinParameters;
  skinDamageParameters: SkinParameters;
  skinRenewalParameters: SkinParameters;
  skinRadianceParameters: SkinParameters;
}

export interface SkinConcernData {
  concern: string;
  lesionAnalysisForConcern: string;
}

export interface ConcernsWithCount {
  infAcne: number;
  comedone: number;
  openPores: number;
  ds: number;
  dc: number;
  melasma: number;
  acneScars: number;
  sensitiveSkin: number;
}
export interface Concerns {
  infAcne: string;
  comedone: string;
  openPores: string;
  ds: string;
  dc: string;
  melasma: string;
  acneScars: string;
  sensitiveSkin: string;
}


interface Assets {
  video: LocalizedSection;
  images: LocalizedSection;
}

interface LocalizedSection {
  en: string[] | string,
  hi: string[] | string,
}

export interface SkinReportDRVideoExperimentType {
  FACE: Assets;
  HAIR: Assets;
}

interface TreatmentPlanExpVariant {
  showHeroIngredients: boolean;
  FACEWASH_MAP: { iTeH6XLTLM: string, KdRMDPkNHr: string, ZCI903r4Vw: string },
  SUPPLEMENT_MAP: { Ha7D8n3FtI: string, SSerUSiEFg: string, '3azLO6BTpf': string, NMwvvHT2A5: string, UMyVJbm3Jq: string }
}

@Component({
  selector: 'report',
  templateUrl: './report.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})

export class ReportComponent {
  static readonly MINIMUM_REQUIRED_HERO_INGREDIENTS = 3;
  static readonly MAXIMUM_REQUIRED_HERO_INGREDIENTS = 6;
  static readonly MINIMUM_REQUIRED_HERO_INGREDIENTS_EXISTING_UI = 4;
  private readonly HAIR_CONCERNS = ['hairline', 'temple-baldness', 'scalp-segment'];
  @Input('regimen') regimen: any;
  @Input('experiments') experiments: any[];
  @Input('isTimerPage') isTimerPage: boolean = false;
  @Input('showRegimenToast') showRegimenToast: boolean = false;
  @Output('hideReport') hideReport: EventEmitter<void> = new EventEmitter();
  @Output('showRegimenTimer') showRegimenTimer: EventEmitter<void> = new EventEmitter();
  user: any;
  detectedConcerns: Array<any> = [];
  previousInstantCheckup: Array<any> = [];
  allocatedDoctor: any;
  treatmentDimension: SkinCareData;
  skinParametersInfo: SkinData;
  lesionAnalysis: [SkinConcernData];
  mergedMap: Map<string, any> = new Map();
  mergedConcerns: Array<any> = [];
  createdAt: Date = new Date();
  problemsCopy: { [key: string]: { [key: string]: { name: string, info: string } } } = problemsCopy;
  updatedPayload: ConcernsWithCount = {
    infAcne: 0,
    comedone: 0,
    openPores: 0,
    ds: 0,
    dc: 0,
    melasma: 0,
    acneScars: 0,
    sensitiveSkin: 0,
  };
  concernNames: Concerns = {
    infAcne: 'Acne',
    comedone: 'Comedones',
    openPores: 'Open pores',
    ds: 'Dark spots',
    dc: 'Dark circles',
    melasma: 'Melasma',
    acneScars: 'Acne Scars',
    sensitiveSkin: 'Sensitive Skin',
  };
  timerInterval: any;
  currentTime: number;
  overallSummary: string;
  mainConcernDetectedByAI: string = 'infAcne';
  reportCode: string;
  patientID: string;
  loading: boolean = false;
  mainConcern: string = '';
  showFirstJourney: boolean = false;
  otherConcerns: string = '';
  showTimer: boolean = false;
  severity: any;
  mainConcernDetections: any;
  userMainConcern: string = '';
  firstFrontFaceInstantCheckup: any;
  showSkinParametersBasedOnRegimen: boolean = false;
  severityIconDisplayMap: { [key: string]: boolean } = {};
  parsedSkinParameters:any = {};
  showHeroIngredients: boolean = true;
  heroIngredients: any[] = [];
  drVideoExpAssets: {video: string | string[], images: [string, string]} | null = null;
  isSleekDrCardExperimentEnabled: boolean = false;
  acqRebranding2025: boolean = false;
  heroIngredientsCount: number = 3;
  isRebrandedHeroIngredients: boolean = false;
  isSkinReportLoadFixesEnabled: boolean = false;
  isNewDoctorUiExperimentEnabled: boolean = false;
  userFirstName: string = '';
  isEnglishUser: boolean = false;
  onReportPageRoute: boolean = false;
  showRebrandedSkinParameters: boolean = false;

  experimentService: ExperimentService = inject(ExperimentService);
  treatmentPlanDescriptionExp: Table.Experiment & { variant: TreatmentPlanExpVariant };

  constructor(public conn: ConnectionService,
    private router: Router,
    public appConfig: AppConfig,
    private changeDetection: ChangeDetectorRef,
    private readonly eventLogger: EventLoggerService,
    private readonly instantCheckupService: InstantCheckupService,
  ) {
    const state = this.router.getCurrentNavigation()?.extras?.state as { regimen?: any; } | undefined;
    if (state?.regimen != null) this.regimen = state.regimen;
  }

  async ngOnInit(): Promise<void> {
    this.onReportPageRoute = this.router.url === '/report' || this.router.url.startsWith('/report?');
    this.loading = true;
    this.user = this.conn.getActingUser();
    this.userFirstName = this.user?.get('PatientName')?.split(' ')[0] || '';
    this.isEnglishUser = this.user?.get('languagePreference') === this.appConfig.Shared.Languages.EN;
    await this.setUserExperiments();
    this.detectedConcerns = this.user?.get('aiDetections') || [];
    // Sometimes allocated doctor obj is not available and template is broken so fetching user details below
    if (!this.user?.get('allocatedDoctor')) await this.conn.getActingUser().fetch();
    this.allocatedDoctor = await this.conn.findUserByObjectId(this.user?.get('allocatedDoctor')?.id);
    this.previousInstantCheckup = await this.conn.fetchInstantCheckup({ userId: this.user.get('username') });
    await this.processCheckupInfo();
    await this.updateReportInfo();
    this.checkForJourney();
    this.loading = false;

    if (this.regimen) {
      this.renderHeroIngredients();
    }
    this.showRebrandedSkinParameters = this.experimentService.shouldShowRebrandedSkinParameters();
    this.changeDetection.detectChanges();
  }

  get isHairReport(): boolean {
    return this.regimen?.class === ApiClientConstant.Regimen.Class.HAIR;
  }

  async setUserExperiments(): Promise<void> {
    const experiments = await this.conn.findUserActiveExperiments();
    experiments?.forEach((exp: any): void => {
      switch (exp.key) {
        case 'skin_report_regimen_and_skin_params_mapping':
          this.showSkinParametersBasedOnRegimen = true;
          break;
        case 'skin_report_dr_video':
          this.processSkinReportDrVideoExperiment(exp);
          break;
        case 'sleek_dr_card':
          this.isSleekDrCardExperimentEnabled = true;
          break;
        case "acq_rebranding_2025":
          this.acqRebranding2025 = true;
          break;
        case 'skin_report_hero_ingredients' :
          this.processHeroIngredientsExperiment(exp?.variant);
          break;
        case 'skin_report_load_fixes':
          this.isSkinReportLoadFixesEnabled = true;
          break;
        case 'skin_report_new_Dr_UI':
          this.isNewDoctorUiExperimentEnabled = true;
          break;
        case 'treatment_plan_description':
          this.treatmentPlanDescriptionExp = exp;
          break;
        default:
          break;
      }
    });
  }

  processSkinReportDrVideoExperiment(exp: {variant: SkinReportDRVideoExperimentType}) {
    const variant = exp.variant;
    const className: 'FACE' | 'HAIR' = this.regimen?.class || 'FACE';
    const language = this.user?.get('languagePreference') || 'en';
    const defaultVideo = ['https://cdn.cureskin.com/app/video/drCharu_Eng_1_skin.mp4'];
    const defaultImages = [
      'https://cdn.cureskin.com/app/img/timer-page-inline-en-1.png',
      'https://cdn.cureskin.com/app/img/timer-page-inline-en-2.png',
    ];
    const video = variant?.[className]?.['video']?.[language] || defaultVideo;
    const images = variant?.[className]?.['images']?.[language] || defaultImages;
    this.drVideoExpAssets = {video, images};
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['regimen']?.currentValue) {
      await this.setUserExperiments();
      this.renderHeroIngredients();
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.timerInterval);
  }

  async updateReportInfo(): Promise<void> {
    if (this.user?.get('report')) {
      const { reportCode, patientID, createdAt }: { reportCode: string, patientID: string, createdAt: Date} = this.user.get('report');
      this.reportCode = reportCode;
      this.patientID = patientID;
      this.createdAt = createdAt;
    } else {
      this.reportCode = `CS${this.generateRandomNumberWithDigits(7)}`;
      this.patientID = `CS${this.generateRandomNumberWithDigits(4)}`;
      await this.saveReportInfo();
    }
  }

  checkForJourney(): void {
    const firstJourney = ['Pigmentation', 'Acne Scars', 'Dark spots'];
    if (firstJourney.includes(this.mainConcern)) {
      this.showFirstJourney = true;
    }
  }

  async saveReportInfo(): Promise<void> {
    this.user?.set('report', {
      reportCode: this.reportCode,
      patientID: this.patientID,
      createdAt: this.createdAt,
    });
    await this.user.save();
  }

  async processCheckupInfo(): Promise<void> {
    this.filterFrontAndSideFace();
    this.getCommonConcernsWithHighCount();
    await this.getSkinReport();
  }

  filterFrontAndSideFace(): void {
    if (this.isSkinReportLoadFixesEnabled && !this.previousInstantCheckup?.length) {
      this.previousInstantCheckup = [];
      return;
    }
    const filteredArray: Array<any> = [];
    const sideFace = this.previousInstantCheckup?.find((checkup: any): boolean => checkup?.type?.includes(ApiClientConstant.InstantCheckup.Type.SIDE_FACE));
    const frontFace = this.previousInstantCheckup?.find((checkup: any): boolean => checkup?.type?.includes(ApiClientConstant.InstantCheckup.Type.FRONT_FACE));

    if (frontFace) {
      filteredArray.push(frontFace);
    }

    if (sideFace) {
      filteredArray.push(sideFace);
    }

    this.previousInstantCheckup = filteredArray;
  }

  getCommonConcernsWithHighCount(): void {
    if (this.isSkinReportLoadFixesEnabled && !this.previousInstantCheckup?.length) {
      this.mergedMap.clear();
      this.mergedConcerns = [];
      return;
    }
    const frontFaceDetection = this.previousInstantCheckup.find(
      (item: any): boolean => item.type === this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE) || [];

    const sideFaceDetection = this.previousInstantCheckup.find(
      (item: any):boolean => item.type !== this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE) || [];

    const frontFaceResults = frontFaceDetection?.aiResponse?.result || [];
    const sideFaceResults = sideFaceDetection?.aiResponse?.result || [];
    const allObjects = [...frontFaceResults, ...sideFaceResults];

    // Iterating over each detected concern (checkup) and merge them based on 'ProblemName'.
    allObjects.forEach((checkup: any): void => {
      const problemName = this.getProblemName(checkup);
      const existingCheckup = this.mergedMap.get(problemName);

      // If no existing checkup is found, or if the current checkup has more BoundingBoxes,
      // update the mergedMap with the current checkup.
      if (!existingCheckup || (checkup.BoundingBoxes?.length || 0) > (existingCheckup.BoundingBoxes?.length || 0)) {
        this.mergedMap.set(problemName, checkup);
      }
    });

    this.mergedMap.forEach((item: any): void => {
      this.mergedConcerns.push(item);
    });
  }

  getProblemName(checkup: any): any {
    if (checkup?.ProblemName) {
      return checkup.ProblemName;
    }
    return this.problemsCopy.en[checkup.class]?.name;
  }

  async getSkinReport(): Promise<void> {
    const concernsWithCount = this.updatePayloadWithDetectedConcernCount();
    const skinReport = await this.conn.getSkinReportInfo(this.mainConcernDetectedByAI, concernsWithCount);
    this.showRegimenTimer.emit();
    this.setSkinReportParams(concernsWithCount, skinReport);
  }

  setSkinReportParams(concernsWithCount:any, skinReport: any): void {
    const skinReportParamsFromRegimen: any = this.regimen?.skinReportParams || {};
    const { treatmentDimension, skinParameters, lesionAnalysis, overallSummary }: any = skinReportParamsFromRegimen;
    const skinReportFallback = {
      treatmentDimension: skinReport?.treatmentDimensions,
      skinParameters: skinReport?.skinParameters,
      lesionAnalysis: skinReport?.lesionAnalysis,
      overallSummary: skinReport?.overallSummary?.overallSummary,
    };
    this.parsedSkinParameters = { ...skinParameters };
    Object.keys(this.parsedSkinParameters).forEach((key: string): void => {
      if (typeof this.parsedSkinParameters[key] === 'string') {
        if (this.parsedSkinParameters[key].trim() === '') {
          this.parsedSkinParameters[key] = { patientScore: '', expectedRange: '' };
        } else {
          try {
            const formattedString = this.parsedSkinParameters[key]
              .replace(/\b(\w+)\b:/g, '"$1":')
              .replace(/\b(\d{1,4})-(\d{1,4})\b/g, '"$1-$2"');

            this.parsedSkinParameters[key] = JSON.parse(formattedString);
          } catch (error) {
            this.parsedSkinParameters[key] = { patientScore: '', expectedRange: '' };
          }
        }
      }
    });
    if (this.showSkinParametersBasedOnRegimen) {
      this.treatmentDimension = treatmentDimension || skinReportFallback?.treatmentDimension;
      this.skinParametersInfo = Object.keys(this.parsedSkinParameters).length > 0 ? this.parsedSkinParameters
        : skinReportFallback?.skinParameters;
      this.lesionAnalysis = lesionAnalysis || skinReportFallback?.lesionAnalysis;
    } else {
      this.treatmentDimension = skinReportFallback?.treatmentDimension;
      this.skinParametersInfo = skinReportFallback?.skinParameters;
      this.lesionAnalysis = skinReportFallback?.lesionAnalysis;
    }

    this.severity = this.capitalizeFirstLetter(this.regimen?.displayConcern?.severity)
      || 'Moderate';
    if (this.regimen?.displayConcern?.name) {
      this.mainConcern = this.regimen?.displayConcern?.name;
    }

    if (overallSummary) {
      this.overallSummary = overallSummary;
    } else if (skinReportFallback?.overallSummary) {
      this.overallSummary = skinReportFallback?.overallSummary;
    } else {
      this.assignFallbackSummary();
      this.logSkinReportParams(concernsWithCount);
    }

    this.firstFrontFaceInstantCheckup = this.getFirstFrontFaceCheckup();
    this.setOtherConcerns();
    this.setSeverity();
  }

  getConcern(concern: string): string {
    if (concern === this.concernNames.infAcne) {
      return this.appConfig.Shared.SkinReport.Concern.INFLAMMATORY_ACNE;
    }
    return concern;
  }

  get getFirstValidDetectedConcern(): string {
    return this.detectedConcerns?.find(
      (concern: string) => !this.HAIR_CONCERNS.some(
        hairConcern => concern.toLowerCase().includes(hairConcern)
      )
    ) ?? '';
  }

  setOtherConcerns(): void {
    const concernString = this.getConcern(this.mainConcern);
    const melasmaSeverity = this.getMelasmaSeverity();
    const melasmaConcern = this.appConfig.Shared.SkinReport.Concern.MELASMA;
    const isMildMelasma = melasmaSeverity === this.appConfig.Shared.InstantCheckup.Severity.MILD;
    const treatmentConcerns: string[] = this.regimen?.displayConcern?.treatmentConcerns ?? [];

    // Convert treatment concern keys (e.g., "DARK_SPOTS_OR_MARK") to display names (e.g., "Dark Spots")
    const treatmentConcernDisplayNames = treatmentConcerns
      .map((tc: string): string => TREATMENT_CONCERN_KEY_TO_DISPLAY_NAME[tc] ?? tc)
      .filter((name: string): boolean => !!name);

    this.otherConcerns = this.detectedConcerns
      .filter((concern: string):boolean => {
        const isMainConcern = concern.toLowerCase().trim() === concernString.toLowerCase().trim();
        const isMelasmaButNotMild = concern === melasmaConcern && !isMildMelasma;
        const isHairConcern = concern.toLowerCase().includes('hairline') || concern.toLowerCase().includes('temple-baldness') || concern.toLowerCase().includes('scalp-segment');
        const isInTreatmentConcerns = treatmentConcernDisplayNames.some(
          (displayName: string): boolean => displayName.toLowerCase().trim() === concern.toLowerCase().trim()
        );
        return !isMainConcern && (concern !== melasmaConcern || isMelasmaButNotMild) && !isHairConcern && !isInTreatmentConcerns;
      }).join(', ');
  }

  getMelasmaSeverity(): string {
    const melasmaDetails = this.firstFrontFaceInstantCheckup?.aiResponse?.result?.find(
      (concernDetails: any):any => concernDetails.ProblemName === this.appConfig.Shared.SkinReport.Concern.MELASMA
        && concernDetails.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED);

    return melasmaDetails?.Severity;
  }

  setSeverity():void {
    this.severity = this.getSeverityFromRegimen() || this.getSeverityFromCheckup();
  }

  private getFirstFrontFaceCheckup(): any {
    if (this.isSkinReportLoadFixesEnabled && !this.previousInstantCheckup?.length) return undefined;
    return this.previousInstantCheckup.find((checkup: any): any => checkup.type === this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE);
  }

  private getSeverityFromRegimen(): string | undefined {
    const severityFromRegimen = this.regimen?.displayConcern?.severity;
    if (severityFromRegimen) {
      return this.capitalizeFirstLetter(severityFromRegimen);
    }
    return undefined;
  }

  private getSeverityFromCheckup(): string {
    this.mainConcernDetections = this.getMainConcernDetections();

    const detectionStatus = this.mainConcernDetections?.Condition
      === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED;

    return detectionStatus
      ? this.instantCheckupService.getSeverityFromConcern(this.mainConcernDetections) ?? this.appConfig.Shared.SkinReport.Severity.MODERATE
      : this.appConfig.Shared.SkinReport.Severity.MODERATE;
  }

  private getMainConcernDetections(): Table.InstantCheckup['attributes']['aiResponse']['result'][number] | undefined {
    const concernString = this.getConcern(this.mainConcern);
    if (!this.firstFrontFaceInstantCheckup) return undefined;
    const checkupResults = this.firstFrontFaceInstantCheckup?.aiResponse?.result;
    return checkupResults?.find((result): boolean => result?.ProblemName === concernString);
  }

  assignFallbackSummary(): void {
    const normalizedMainConcern = this.mainConcern?.toLowerCase() || '';
    const matchingKey = Object.keys(FallbackSummaryMap).find(
      (key: string): boolean => key.toLowerCase() === normalizedMainConcern
    );
    this.overallSummary = matchingKey ? FallbackSummaryMap[matchingKey] : FallbackSummaries.GENERAL;
  }

  public logSkinReportParams(concernsWithCount: any): void {
    this.eventLogger.trackInElasticSearch({
      event: 'SKIN_REPORT_PARAM_EMPTY',
      type: 'ERROR_SKIN_REPORT_PARAM_EMPTY',
      user: this.user?.get('username'),
      data: JSON.stringify(concernsWithCount),
    });
  }

  capitalizeFirstLetter(str: string): string {
    if (!str) return '';
    return str.split(' ').map((item: string):string => item[0] + item.slice(1).toLowerCase()).join(' ');
  }
  updatePayloadWithDetectedConcernCount(): ConcernsWithCount {
    // Mapping the threshold values based on problem names
    this.mergedMap.forEach((obj: any): void => {
      this.processCheckup(obj);
    });
    return this.updatedPayload;
  }

  processCheckup(obj: any): void {
    switch (obj?.ProblemName) {
      case 'Inflammatory Acne':
        this.updateInflammatoryAcne(obj);
        break;
      case 'Comedones':
        this.updateComedones(obj);
        break;
      case 'Acne Scars':
        this.updateAcneScars(obj);
        break;
      case 'Dark Spots':
        this.updateDarkSpots(obj);
        break;
      case 'Open Pores':
        this.mainConcernDetectedByAI = 'openPores';
        if (obj?.Condition === 'Detected') {
          this.updatedPayload.openPores = 1;
          this.updateMainConcern('openPores');
        }
        break;
      case 'Melasma':
        this.mainConcernDetectedByAI = 'melasma';
        if (obj?.Condition === 'Detected') {
          this.updatedPayload.melasma = 1;
          this.updateMainConcern('melasma');
        }
        break;
      case 'Dark Circles':
        this.mainConcernDetectedByAI = 'dc';
        if (obj?.Condition === 'Detected') {
          this.updatedPayload.dc = 1;
          this.updateMainConcern('dc');
        }
        break;
      default:
        break;
    }
  }

  updateInflammatoryAcne(obj: any): void {
    if (obj?.Condition !== 'Detected') return;
    this.mainConcernDetectedByAI = 'infAcne';
    this.updateMainConcern('infAcne');
    const boundingBoxesCount = obj?.BoundingBoxes?.length ?? 0;
    this.updatedPayload.infAcne = boundingBoxesCount;
  }

  updateComedones(obj: any): void {
    if (obj?.Condition !== 'Detected') return;
    this.mainConcernDetectedByAI = 'comedone';
    this.updateMainConcern('comedone');
    const boundingBoxesCount = obj?.BoundingBoxes?.length ?? 0;
    this.updatedPayload.comedone = boundingBoxesCount;
  }

  updateAcneScars(obj: any): void {
    if (obj?.Condition !== 'Detected') return;
    this.mainConcernDetectedByAI = 'acneScars';
    this.updateMainConcern('acneScars');
    const boundingBoxesCount = obj?.BoundingBoxes?.length ?? 0;
    this.updatedPayload.acneScars = boundingBoxesCount;
  }

  updateDarkSpots(obj: any): void {
    if (obj?.Condition !== 'Detected') return;
    this.mainConcernDetectedByAI = 'ds';
    this.updateMainConcern('ds');
    const boundingBoxesCount = obj?.BoundingBoxes?.length ?? 0;
    this.updatedPayload.ds = boundingBoxesCount;
  }

  generateRandomNumberWithDigits(digits: number): string {
    const digitMultiplier = 10 ** (digits - 1);
    const randomNumber = Math.floor(Math.random() * 9 * digitMultiplier) + digitMultiplier;
    return randomNumber.toString();
  }

  back(): void {
    this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
  }

  updateMainConcern(concern: string): void {
    if (!this.regimen?.displayConcern?.name || !this.mainConcern) this.mainConcern = this.concernNames[concern];
  }

  skipConcern(checkup: any): boolean {
    const concernsThatShouldBeSkipped = ['Puffy Eyes', 'Facial Hair', 'Perioral Pigmentation', 'Other Scars'];
    const problemName = this.getProblemName(checkup);
    if (concernsThatShouldBeSkipped.includes(problemName)) return true;
    return false;
  }

  setSeverityIconDisplayState(skinParam:string, event:boolean): void {
    this.severityIconDisplayMap[skinParam] = event;
  }

  renderHeroIngredients(): void {
    if (!this.regimen?.variants) {
      this.showHeroIngredients = false;
      return;
    }
    const variantWithFiveProducts = this.regimen?.variants?.find((variant: any): boolean => variant?.variantId === 'variant_1999');
    const regimenProducts = extractRegimenProducts(variantWithFiveProducts);
    this.heroIngredients = extractHeroIngredients(regimenProducts) || [];
    if (this.isRebrandedHeroIngredients && this.heroIngredients.length > 0) {
      this.heroIngredients = this.heroIngredients.slice(0, this.heroIngredientsCount);
      this.showHeroIngredients = this.heroIngredients.length >= ReportComponent.MINIMUM_REQUIRED_HERO_INGREDIENTS;
    } else {
      this.heroIngredients = this.heroIngredients.slice(0, ReportComponent.MAXIMUM_REQUIRED_HERO_INGREDIENTS);
      this.showHeroIngredients = this.heroIngredients.length === ReportComponent.MINIMUM_REQUIRED_HERO_INGREDIENTS
        || this.heroIngredients.length > ReportComponent.MINIMUM_REQUIRED_HERO_INGREDIENTS_EXISTING_UI;
    }
    this.changeDetection.detectChanges();
  }

  processHeroIngredientsExperiment(variant: { numberOfIngredients: number }): void {
    if (!variant?.numberOfIngredients) return;

    this.heroIngredientsCount = variant.numberOfIngredients;
    this.isRebrandedHeroIngredients = true;
  }
}
